"use client"

import { useEffect, useMemo, useRef, useState } from "react"

import { ApiService } from "@/lib/api"
import { DEFAULT_MAX_AVAILABLE_SLOTS_DAYS } from "@/lib/constants/timezones"
import type { AvailableSlotItem } from "@/lib/types/appointment.types"
import { isSlotInPastBusiness, resolveBusinessTimezone } from "@/lib/utils/business-timezone"
import { resolveAvailableSlots } from "@/lib/appointments.utils"
import { useStore } from "@/app/components/common/store.provider"

export type BookingAvailability = {
  availableDays: Set<string>
  loadingDays: boolean
  slots: AvailableSlotItem[]
  loadingSlots: boolean
}

/**
 * Boundary sandbox for the availability endpoints. Owns settings hydration,
 * the day-range lookup and the per-day slot lookup, and keeps the store's
 * active date/time selection pointed at something bookable. Shared by the
 * single and group flows so both behave identically.
 */
export function useBookingAvailability(): BookingAvailability {
  const { appointmentStore } = useStore()

  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [availableDays, setAvailableDays] = useState<Set<string>>(new Set())
  const [loadingDays, setLoadingDays] = useState(true)
  const [slots, setSlots] = useState<AvailableSlotItem[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const settingsRequestedRef = useRef(false)

  const isCustomBooking = appointmentStore.activeIsCustomBooking
  const daysRange = appointmentStore.maxAvailableSlotsDays
  const businessTz = appointmentStore.businessTimezone
  const selectedDate = appointmentStore.activeDate
  const serviceIdsKey = appointmentStore.activeServices
    .map((service) => `${service.id}:${service.duration}`)
    .join(",")

  const serviceIds = useMemo(
    () =>
      serviceIdsKey
        .split(",")
        .filter(Boolean)
        .map((part) => Number(part.split(":")[0]))
        .filter((id) => Number.isFinite(id) && id > 0),
    [serviceIdsKey],
  )

  const durationMinutes = useMemo(() => {
    const total = serviceIdsKey
      .split(",")
      .filter(Boolean)
      .reduce((sum, part) => sum + (Number(part.split(":")[1]) || 0), 0)
    return total > 0 ? total : 60
  }, [serviceIdsKey])

  useEffect(() => {
    // The lock survives React's double-mount, so the result must not be gated
    // on the first mount still being alive or settings would never load.
    if (settingsRequestedRef.current) return
    settingsRequestedRef.current = true

    void (async () => {
      try {
        const settings = await ApiService.getGeneralSettings()
        appointmentStore.setBookingSettings(
          resolveBusinessTimezone(settings.timezone),
          settings.maxAvailableSlotsDays ?? DEFAULT_MAX_AVAILABLE_SLOTS_DAYS,
          settings.maxGroupBookingSize,
        )
      } catch {
        // Keep store defaults when settings are unavailable.
      } finally {
        setSettingsLoaded(true)
      }
    })()
  }, [appointmentStore])

  useEffect(() => {
    if (isCustomBooking) {
      setAvailableDays(new Set())
      setLoadingDays(false)
      return
    }
    if (!settingsLoaded) return

    let mounted = true
    void (async () => {
      setLoadingDays(true)
      try {
        const res = await ApiService.getAvailableSlotsRange(daysRange, {
          serviceIds,
          durationMinutes,
        })
        if (!mounted) return
        if (res.timezone) {
          appointmentStore.setBookingSettings(
            res.timezone,
            appointmentStore.maxAvailableSlotsDays,
          )
        }
        const available = new Set<string>()
        for (const day of res.days ?? []) {
          if (day.hasAvailability) available.add(day.day)
        }
        setAvailableDays(available)

        if (!appointmentStore.activeDate && available.size > 0) {
          appointmentStore.setActiveDate([...available].sort()[0])
        }
      } catch {
        if (mounted) setAvailableDays(new Set())
      } finally {
        if (mounted) setLoadingDays(false)
      }
    })()

    return () => {
      mounted = false
    }
  }, [
    appointmentStore,
    daysRange,
    durationMinutes,
    isCustomBooking,
    serviceIds,
    settingsLoaded,
  ])

  useEffect(() => {
    if (isCustomBooking || !selectedDate) {
      setSlots([])
      setLoadingSlots(false)
      return
    }

    let mounted = true
    void (async () => {
      setLoadingSlots(true)
      try {
        const res = await ApiService.getAvailableSlots(selectedDate, {
          serviceIds,
          durationMinutes,
        })
        if (!mounted) return
        if (res.timezone) {
          appointmentStore.setBookingSettings(
            res.timezone,
            appointmentStore.maxAvailableSlotsDays,
          )
        }
        const nextSlots = resolveAvailableSlots(res)
        setSlots(nextSlots)

        const isBookable = (slot: AvailableSlotItem) =>
          slot.isAvailable &&
          !isSlotInPastBusiness(slot.startTime, selectedDate, businessTz)

        const current = appointmentStore.activeStartTime
        const currentStillValid =
          Boolean(current) &&
          nextSlots.some((slot) => slot.startTime === current && isBookable(slot))

        if (!currentStillValid) {
          const firstAvailable = nextSlots.find(isBookable)
          appointmentStore.setActiveStartTime(firstAvailable?.startTime ?? null)
        }
      } catch {
        if (mounted) setSlots([])
      } finally {
        if (mounted) setLoadingSlots(false)
      }
    })()

    return () => {
      mounted = false
    }
  }, [
    appointmentStore,
    businessTz,
    durationMinutes,
    isCustomBooking,
    selectedDate,
    serviceIds,
  ])

  return { availableDays, loadingDays, slots, loadingSlots }
}
